import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Table, Tooltip, Divider, Spin, Modal, Checkbox, message } from 'antd';
import moment from 'moment';

import i18n from 'i18next';
import { ze } from 'utils/zhEn';
import { CS_STYLES } from 'utils/constants/style-constants';
import RestHelper from 'utils/RestHelper';
import { downloadAsFile } from 'utils/file-utils';
import { selectCurrentAppServerAppSettings } from 'utils/selectors/selectors';
import { ServerAppSettings } from 'utils/server-app-settings';
import { ExceptionCategory, ExceptionCategoryUtil } from 'utils/exception-category';
import { getIssueDetailUrlPrefix } from 'utils/helper';

import IssueCrashFilterEx from 'components/commons/IssueCrashFilter/IssueCrashFilterEx';
import { FieldName, QueryType } from 'components/commons/IssueCrashFilter/IssueCrashFilterExUtil';
import { SearchConditionGroupUtil } from 'components/commons/IssueCrashFilter/SearchConditionGroupUtil';
import CardHead from 'components/commons/CardHead';
import CsCollapseButton from 'components/commons/CsCollapseButton/CsCollapseButton';
import CsDownloadButton from 'components/commons/CsDownloadButton/CsDownloadButton';
import MetricKitFlameGraphModal from 'components/exception/MetricKitPage/MetricKitFlameGraphModal';
import summaryStyle from 'components/exception/ProductSummaryPage/style.scss';
// 复用 OOM 详情列表的 copyText / copyButton 样式，保持视觉一致（详见 deviceId / expUid 列 render）
import oomStyle from 'components/exception/OomDetailedList.scss';

import IconAllRecords from 'svg/v2/newcs_dashboard_advancedsearch_allrecords_icon.svg';
import ConfiguredisplaycolumnsIcon from 'svg/v2/newcs_dashboard_oom_configuredisplaycolumns_icon_normal.svg';

const DEFAULT_PAGE_SIZE = 20;
const REL_DATETIME_THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1000;

// 详情过滤器支持的字段白名单（沿用 TrendComparePage 758-773 的设计，剔除 issue 相关，补充时间字段）。
// FieldName.crashTime / crashUploadTime 是 AdvancedTrendSearchField 新增的两个字段。
//
// 已显式排除（曾经在白名单里、后被移除的）：
// - FieldName.bundleId：MetricKit 数据上不支持按 bundleId 过滤。
// - FieldName.userId：同上，不支持。
// - FieldName.model（"设备商品名"）：与 FieldName.iosModel（"机型 Model"）重复，且按 IssueCrashFilterEx
//     里第 354 行的注释"安卓/iOS 的原始 model 已经废弃，改用机型 Model 字段"——iOS 平台下应当只保留 iosModel。
const METRICKIT_FILTER_FIELD_WHITELIST = [
  FieldName.crashTime,
  FieldName.crashUploadTime,
  FieldName.version,
  FieldName.cpu,
  FieldName.gpu,
  FieldName.countryOrRegion,
  FieldName.androidModel,
  FieldName.iosModel,
  FieldName.vmStatus,
  FieldName.osVersion,
  FieldName.deviceId,
  FieldName.customKey,
];

// 所有可配置展示列的 key（按截图顺序）。
// 注 1：本期 user / userSceneTag 后端虽然 entity 上有字段，但实际不上报，对前端无意义，故先不展示也不做配置入口。
// 注 2：launchTime / runTime 这两列已下线——MetricKit 表里没有 launchTime 字段，本来想用 crashTime - runTime
//      反推；但实际产品确认这两个字段对 MetricKit 上报场景意义不大，且数据精度也不可靠，干脆去掉。
const ALL_COLUMN_KEYS = [
  'crashTime',
  'uploadTime',
  'model',
  'deviceId',
  'expUid',
  'productVersion',
  'os',
  'operation',
];

/**
 * 按异常大类（exceptionCategory）选择 crashTime 的渲染精度。MetricKit 上游数据精度因 category 而异：
 * - OOM：crashTime 仅精确到日（时分秒恒为 0），渲染完整时分秒只会得到 'YYYY-MM-DD 00:00:00' 噪音
 * - 其他（CRASH/HANG）：crashTime 精确到分钟（秒恒为 0），渲染秒级会有多余的 ':00' 噪音
 *
 * 因此分别只渲染到数据真实精度对应的位数。uploadTime 列不受此影响（后端 transformEntityToItemMap 已经
 * 直接给出 'yyyy-MM-dd HH:mm:ss' 字符串，且 uploadTime 是接收端时间，秒级真实可信）。
 */
function formatCrashTimeForCategory(ms, exceptionCategory) {
  if (ms == null || ms === 0 || ms === '0') return '-';
  const num = Number(ms);
  if (!Number.isFinite(num) || num <= 0) return '-';
  if (ExceptionCategoryUtil.isOom(exceptionCategory)) {
    return moment(num).format('YYYY-MM-DD');
  }
  return moment(num).format('YYYY-MM-DD HH:mm');
}

/**
 * MetricKit 下载工具，抽取为模块级导出以便其他组件（如 ReportDetail）复用下载逻辑，
 * 避免在多处重复实现接口调用与文件下载。
 */
export const MetricKitDetailListUtil = Object.freeze({
  /** 把任意值（已经是 Object 或者还是 JSON 字符串）格式化成缩进 4 空格的可读 JSON 文本，用于下载。 */
  formatContent(value) {
    return formatMetricKitContent(value);
  },

  /**
   * 按 crashId 调用 queryMetricKitDetail 拉取原始数据，格式化后触发浏览器下载（MetricKit_{crashId}.txt）。
   * @param {{ appId: string, crashId: string }} params
   */
  async downloadRawDataForCrashId({ appId, crashId }) {
    if (!crashId || !appId) return;
    const hideLoading = message.loading(ze('正在拉取 MetricKit 数据...', 'Fetching MetricKit data...'), 0);
    try {
      const rsp = await RestHelper.mustPost('/api/crash/queryMetricKitDetail', {
        appId,
        crashIdList: [crashId],
      });
      const data = rsp.json?.data || {};
      const item = (data.items || [])[0] || null;
      const content = formatMetricKitContent(item ? item.metricKit : null);
      if (!content) {
        message.warning(ze('该上报没有 MetricKit 数据', 'No MetricKit data for this report'));
        return;
      }
      downloadAsFile(`MetricKit_${crashId}.txt`, content);
      message.success(ze('已开始下载', 'Download started'));
    } catch (e) {
      message.error(ze('拉取 MetricKit 数据失败', 'Failed to fetch MetricKit data'));
    } finally {
      hideLoading();
    }
  },
});

/** 把任意值（已经是 Object 或者还是 JSON 字符串）格式化成缩进 4 空格的可读 JSON 文本，用于下载。 */
function formatMetricKitContent(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'string') {
    // 后端反序列化失败时会原样返回 JSON 字符串，这里兜底再 try parse 一次让缩进生效
    try {
      return JSON.stringify(JSON.parse(value), null, 4);
    } catch (e) {
      return value;
    }
  }
  try {
    return JSON.stringify(value, null, 4);
  } catch (e) {
    return String(value);
  }
}

/** 复制文本到剪贴板 + 成功 toast，用于 deviceId / expUid 列的复制按钮。沿用 OomDetailedList 的同名实现。 */
async function copyTextToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    message.success(ze('已复制ID至剪贴板', 'ID copied to clipboard'));
  } catch (e) {
    message.error(ze('复制失败', 'Copy failed'));
  }
}

/**
 * MetricKit 单条详情列表（含 IssueCrashFilterEx 搜索器、Table、可配置展示列、展开行）。
 *
 * 后端接口：POST /api/crash/queryMetricKitReports
 * 表：dwd_bi_metrickit_v1（在 statrealtime 库下）
 * 字段映射：复用 AdvancedTrendSearchField（已补 crashTime/crashUploadTime/userId/deviceId）
 *
 * @param exceptionCategory 上方 tab 当前选中的异常大类（CRASH/HANG/OOM 等，来自 ExceptionCategory enum）。
 *   提交查询时会通过 SearchConditionGroupUtil.addMustCondition 自动追加一条
 *   {field: exceptionCategory, queryType: TERMS, terms: [大类]} 的硬性条件，保证查到的明细只属于当前 tab。
 */
const MetricKitDetailList = ({ appId, platformId, exceptionCategory }) => {
  const serverAppSettings = useSelector(selectCurrentAppServerAppSettings) || {};
  const advancedTrendCustomKeyWhitelist = serverAppSettings[ServerAppSettings.keys.advancedTrendCustomKeyWhitelist] || [];

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [isExpanded, setIsExpanded] = useState(true);
  const [columnConfigVisible, setColumnConfigVisible] = useState(false);
  const [visibleColumnKeys, setVisibleColumnKeys] = useState([...ALL_COLUMN_KEYS]);
  const [modelProductNameMap, setModelProductNameMap] = useState({});
  const [flameGraphModalVisible, setFlameGraphModalVisible] = useState(false);
  const [flameGraphData, setFlameGraphData] = useState(null);


  // 默认条件：最近 3 天的 crashUploadTime（不可删除）。
  // 用 crashUploadTime 而不是 crashTime，是为了能用上 CK 表的分区裁剪（partition by toYYYYMM(uploadTime)），查询更快。
  const [filterConditions, setFilterConditions] = useState([
    {
      field: FieldName.crashUploadTime,
      queryType: QueryType.RANGE_RELATIVE_DATETIME,
      gte: REL_DATETIME_THREE_DAYS_MS,
    },
  ]);
  // pendingFilterConditions = 用户在过滤器里改了但还没点"查询"的条件；提交时同步到 filterConditions 并触发 fetch
  const [pendingFilterConditions, setPendingFilterConditions] = useState(filterConditions);

  // 用户可见的字段白名单。advancedTrendCustomKeyWhitelist 配置后才放开 customKey。
  const fieldWhitelist = useMemo(() => {
    if (advancedTrendCustomKeyWhitelist.length > 0) {
      return METRICKIT_FILTER_FIELD_WHITELIST;
    }
    return METRICKIT_FILTER_FIELD_WHITELIST.filter(x => x !== FieldName.customKey);
  }, [advancedTrendCustomKeyWhitelist]);

  // 拉取版本下拉选项（供 version 字段使用）。
  //
  // 直接复用 redux 里 summary.selectOptions.rankVersion.options——它由 actions.getOptions('version')
  // 拉取后写入（见 summaryReducer.GETSELECTOR_GET_SUCC），MetricKitPage 在挂载时已经触发过这个 action。
  // 这里**不能**自己再调 RestHelper.get('/getSelector...').then(.map(x => ({value, label})))，
  // 因为 .map 会把后端返回的 `millisSinceUpdate` / `versionAlias` 字段砍掉，下游
  // getTreeSelectVersionOptions 没有这两个字段就跑不出"折叠脏版本到其他"的逻辑、也无法做别名搜索匹配。
  // 这就是趋势页的版本下拉看起来"会自动折叠老版本到其他"，OomPage / 旧 MetricKit 实现却没这个效果的根因。
  //
  // OomPage 自己 fetch + .map 同样有这个 bug，但是项目接入数据有限暂时未暴露，本次不顺手改它，避免把范围撑大。
  // 注意：这里**不再附加 { value: '-1', label: '全版本' }** 这一项——
  // filterOptions.version 是直接给 getTreeSelectVersionOptions 当 versionList 输入用的（见 FieldValuePart.jsx
  // 第 132-142 行），加 '-1' 这种伪节点会被当成一个版本号塞进树里，污染树结构。
  // 选择器内部已经天然支持"不选择 = 不过滤"，无需提供"全版本"选项。
  const rankVersionOptions = useSelector((state) => {
    const opts = state.summary.get('selectOptions').get('rankVersion');
    if (!opts) return [];
    // rankVersion.options 在 redux 里是 immutable List，转成原生数组；初始化态可能是 List([{label, value:'-1'}]) 占位
    const arr = (opts.get ? opts.get('options') : opts.options) || [];
    const jsArr = arr.toJS ? arr.toJS() : arr;
    // 过滤掉 summaryInitialState 的 '-1' 占位项（真实版本号不会是 '-1'）
    return jsArr.filter(x => x && x.value !== '-1');
  });
  const filterOptions = useMemo(() => ({
    version: rankVersionOptions,
  }), [rankVersionOptions]);

  // 查询单条详情列表
  const fetchList = useCallback(async (overrides = {}) => {
    if (!appId || !platformId) return;
    const conditions = overrides.conditions || filterConditions;
    const offset = ((overrides.page || page) - 1) * (overrides.pageSize || pageSize);
    const limit = overrides.pageSize || pageSize;

    // 在用户可编辑的 conditions 之外，再硬性追加一条 exceptionCategory 限制，
    // 保证查到的明细只属于当前 tab 选中的异常大类（CRASH/HANG/OOM/METRICKIT_CPU/METRICKIT_DISK）。
    // 后端 AdvancedTrendSearchField.exceptionCategory 会 .toLowerCase() 再去匹配 CK 列。
    let searchConditionGroup = { conditions };
    if (exceptionCategory) {
      searchConditionGroup = SearchConditionGroupUtil.addMustCondition(searchConditionGroup, {
        field: FieldName.exceptionCategory,
        queryType: QueryType.TERMS,
        terms: [exceptionCategory],
      });
    }

    setLoading(true);
    try {
      const rsp = await RestHelper.mustPost('/api/crash/queryMetricKitReports', {
        appId,
        platformId,
        offset,
        limit,
        sortField: 'uploadTime',
        desc: true,
        search: {
          searchConditionGroup,
        },
      });
      // 注意：本接口走 PhpCrashController（和 queryOomMmapCrashes 同一类），响应包装层是 `data`，不是 `ret`。
      // 历史上踩过坑：误用 rsp.json.ret 会拿到 undefined → 表格永远空。参考 OomPage.jsx#queryOomCrashes。
      const data = rsp.json?.data || {};
      setItems((data.items || []).map((x, i) => ({ ...x, _rowKey: `${offset + i}` })));
      setTotal(Number(data.total) || 0);
      setModelProductNameMap(data.modelProductNameMap || {});
    } catch (e) {
      message.error(ze('查询 MetricKit 数据失败', 'Failed to query MetricKit data'));
    } finally {
      setLoading(false);
    }
  }, [appId, platformId, filterConditions, page, pageSize, exceptionCategory]);

  // 初次进入、翻页、或切换异常大类 tab 时，重新拉数据
  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId, platformId, page, pageSize, exceptionCategory]);

  /**
   * 点击操作列"下载 MetricKit 原始数据"链接时触发，委托给 MetricKitDetailListUtil.downloadRawDataForCrashId。
   *
   * <p><b>为什么是直接下载、而不是弹 Modal 显示</b>：MetricKit 原始 payload 是 Apple
   * 给机器消费的格式（直方图用 buckets 表达、callStackTree 嵌套很深且地址未符号化），
   * 直接 pretty-print 给人看几乎不可读。本期先让用户能"拿到"原始数据（用 atos / Xcode
   * Crashes Organizer 等外部工具进一步处理），后续做用户友好展示见 §10 TODO。
   */
  const downloadMetricKitRawData = useCallback(async (record) => {
    await MetricKitDetailListUtil.downloadRawDataForCrashId({ appId, crashId: record?.crashId });
  }, [appId]);

  /**
   * 点击"查看火焰图"按钮（仅 METRICKIT_CPU / METRICKIT_DISK 大类）：
   * 先拉取单条详情的 metricKit 原始字段，再请求 re-retrace 生成火焰图数据。
   * 当前阶段先获取数据并输出到控制台，待确认返回结构后再决定渲染方式。
   */
  const handleViewFlameGraph = useCallback(async (record) => {
    const hideLoading = message.loading(ze('正在获取火焰图数据...', 'Fetching flame graph data...'), 0);
    try {
      // Step 1: 拉取含 metricKit 大字段的单条详情（列表接口出于性能不返回此字段）
      const detailRsp = await RestHelper.mustPost('/api/crash/queryMetricKitDetail', {
        appId,
        crashIdList: [record.crashId],
      });
      const detailData = detailRsp.json?.data || {};
      const item = (detailData.items || [])[0] || null;
      const metricKit = item ? item.metricKit : null;
      if (!metricKit) {
        message.warning(ze('该上报没有 MetricKit 数据', 'No MetricKit data for this report'));
        return;
      }
      const rawJson = typeof metricKit === 'string' ? metricKit : JSON.stringify(metricKit);

      // Step 2: 请求 re-retrace 生成火焰图数据
      const diagnosticType = exceptionCategory === ExceptionCategory.METRICKIT_CPU ? 'cpu' : 'disk';
      const fgRsp = await RestHelper.mustPost('/api/crash/getMetricKitFlameGraph', {
        appId,
        diagnosticType,
        rawJson,
        appVersion: record.productVersion || '',
      });
      const fgData = fgRsp.json?.data;
      if (!fgData || !Array.isArray(fgData.roots) || fgData.roots.length === 0) {
        message.warning(ze('未获取到火焰图数据', 'No flame graph data returned'));
        return;
      }
      // 用表格行的 os 字段（后端已格式化）覆盖 meta 里的原始系统版本，保持显示一致
      const fgDataNormalized = record.os
        ? { ...fgData, meta: { ...(fgData.meta || {}), osVersion: record.os } }
        : fgData;
      setFlameGraphData(fgDataNormalized);
      setFlameGraphModalVisible(true);
    } catch (e) {
      message.error(ze('获取火焰图数据失败', 'Failed to fetch flame graph data'));
    } finally {
      hideLoading();
    }
  }, [appId, exceptionCategory]);

  /**
   * 点击"关联崩溃" / "关联卡死"按钮：通过 queryCrashList 接口查询对应的上报，
   * 找到后在新标签页打开详情页；找不到时弹出错误提示。
   *
   * 当 item 有 expUid 时，用 expUid + exceptionCategory 精准定位；
   * 当 item 缺少 expUid（旧版本 SDK）时，退而使用 crashTime 精确范围 + deviceId + exceptionCategory。
   *
   * exceptionCategory 查询条件说明：
   * - 崩溃 tab（prop CRASH）→ 查 METRICKIT_CRASH（MetricKit 崩溃拆出后的独立大类）
   * - 卡死 tab（prop HANG） → 查 HANG（HANG 本就来自 MetricKit，无 METRICKIT_HANG 类型）
   */
  const handleRelatedReport = useCallback(async (record) => {
    // 根据当前 tab 的 exceptionCategory 映射到 queryCrashList 里实际使用的查询值
    const queryCategory = exceptionCategory === ExceptionCategory.CRASH
      ? ExceptionCategory.METRICKIT_CRASH
      : ExceptionCategory.HANG;

    const hideLoading = message.loading(ze('正在查询关联上报...', 'Searching for related report...'), 0);
    try {
      let conditions;
      if (record.expUid) {
        conditions = [
          { field: FieldName.expUid, queryType: QueryType.TERM, term: record.expUid },
          { field: FieldName.exceptionCategory, queryType: QueryType.TERM, term: queryCategory },
        ];
      } else if (record.crashTime && record.deviceId) {
        conditions = [
          { field: FieldName.crashTime, queryType: QueryType.RANGE, gte: record.crashTime, lte: record.crashTime },
          { field: FieldName.deviceId, queryType: QueryType.TERM, term: record.deviceId },
          { field: FieldName.exceptionCategory, queryType: QueryType.TERM, term: queryCategory },
        ];
      } else {
        message.error(ze(
          `关联上报所需字段缺失：${!record.crashTime ? '发生时间' : ''}${!record.crashTime && !record.deviceId ? '、' : ''}${!record.deviceId ? '设备ID' : ''}`,
          `Required field(s) missing: ${[!record.crashTime && 'Crash Time', !record.deviceId && 'Device ID'].filter(Boolean).join(', ')}`,
        ));
        return;
      }

      const rsp = await RestHelper.mustPost('/api/crash/queryCrashList', {
        appId,
        platformId,
        start: 0,
        rows: 10,
        searchConditionGroup: { conditions },
      });

      const crashDataList = Object.values(rsp.json.ret.crashDatas || {});
      if (crashDataList.length === 0) {
        message.error(ze('未找到关联上报', 'No related report found'));
        return;
      }

      const { crashId: relCrashId, issueId: relIssueId } = crashDataList[0];
      const urlPrefix = getIssueDetailUrlPrefix(queryCategory);
      window.open(`/${urlPrefix}/${appId}/${relIssueId}/report-id/${relCrashId}?pid=${platformId}`, '_blank');
    } catch (e) {
      message.error(ze('查询关联上报失败', 'Failed to query related report'));
    } finally {
      hideLoading();
    }
  }, [appId, platformId, exceptionCategory]);

  function onClickSearch() {
    setFilterConditions(pendingFilterConditions);
    setPage(1);
    setTimeout(() => fetchList({ conditions: pendingFilterConditions, page: 1 }), 0);
  }

  const allColumns = useMemo(() => [
    {
      title: ze('发生时间', 'Crash Time'),
      dataIndex: 'crashTime',
      key: 'crashTime',
      width: 160,
      render: v => formatCrashTimeForCategory(v, exceptionCategory),
    },
    {
      // 上报时间字段对齐后端 AdvancedTrendSearchField.crashUploadTime → CK uploadTime（DateTime 列）。
      // 后端 transformEntityToItemMap 已经把 entity.uploadTime 格式化为 'yyyy-MM-dd HH:mm:ss' 字符串，前端直接展示。
      title: ze('上报时间', 'Upload Time'),
      dataIndex: 'uploadTime',
      key: 'uploadTime',
      width: 160,
      render: v => v || '-',
    },
    {
      title: ze('设备型号', 'Device Model'),
      dataIndex: 'hardware',
      key: 'model',
      width: 180,
      render: (hardware, record) => {
        const product = record.modelProductName || modelProductNameMap[hardware] || hardware;
        return (
          <Tooltip title={hardware}>
            <span>
              {product}
              {product !== hardware && <><br /><span style={{ color: '#999', fontSize: 12 }}>({hardware})</span></>}
            </span>
          </Tooltip>
        );
      },
    },
    {
      // 设备ID / 客户端上报ID 都是长字符串，对齐 OomDetailedList 的展示模式：
      // 截断 + 复制按钮，点击文本本身没有跳转（MetricKit 没有"按 deviceId 查询"页面），
      // 所以仅 expUid 一行的纯展示，加复制按钮放右边。
      title: ze('设备ID', 'Device ID'),
      dataIndex: 'deviceId',
      key: 'deviceId',
      width: 140,
      render: (deviceId) => {
        if (!deviceId) return '-';
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title={deviceId}>
              <div className={oomStyle.copyText} onClick={() => copyTextToClipboard(deviceId)}>{deviceId}</div>
            </Tooltip>
            <div className={oomStyle.copyButton} onClick={() => copyTextToClipboard(deviceId)} />
          </div>
        );
      },
    },
    {
      title: i18n.t('issueCrashFilterKey.expUid'),
      dataIndex: 'expUid',
      key: 'expUid',
      width: 140,
      render: (expUid) => {
        if (!expUid) return '-';
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title={expUid}>
              <div className={oomStyle.copyText} onClick={() => copyTextToClipboard(expUid)}>{expUid}</div>
            </Tooltip>
            <div className={oomStyle.copyButton} onClick={() => copyTextToClipboard(expUid)} />
          </div>
        );
      },
    },
    {
      title: ze('版本', 'Version'),
      dataIndex: 'productVersion',
      key: 'productVersion',
      width: 140,
      ellipsis: true,
      render: v => v || '-',
    },
    {
      title: ze('系统版本', 'OS Version'),
      dataIndex: 'os',
      key: 'os',
      width: 140,
      ellipsis: true,
      render: v => v || '-',
    },
    {
      title: ze('操作', 'Action'),
      dataIndex: 'operation',
      key: 'operation',
      width: 260,
      render: (_v, record) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {exceptionCategory === ExceptionCategory.CRASH && (
            <a
              style={{ color: CS_STYLES.PRIMARY_COLOR }}
              onClick={() => handleRelatedReport(record)}
            >
              {ze('关联崩溃', 'Related Crash')}
            </a>
          )}
          {exceptionCategory === ExceptionCategory.HANG && (
            <a
              style={{ color: CS_STYLES.PRIMARY_COLOR }}
              onClick={() => handleRelatedReport(record)}
            >
              {ze('关联卡死', 'Related Hang')}
            </a>
          )}
          {(exceptionCategory === ExceptionCategory.METRICKIT_CPU || exceptionCategory === ExceptionCategory.METRICKIT_DISK) && (
            <a
              style={{ color: CS_STYLES.PRIMARY_COLOR }}
              onClick={() => handleViewFlameGraph(record)}
            >
              {ze('查看火焰图', 'View Flame Graph')}
            </a>
          )}
          <a
            style={{ color: CS_STYLES.PRIMARY_COLOR }}
            onClick={() => downloadMetricKitRawData(record)}
          >
            {ze('下载 MetricKit 原始数据', 'Download Raw MetricKit Data')}
          </a>
        </span>
      ),
    },
  ], [modelProductNameMap, downloadMetricKitRawData, handleRelatedReport, handleViewFlameGraph, exceptionCategory]);

  // 按用户配置过滤显示列；若当前页所有数据的 expUid 均为空，则额外隐藏该列
  const hasNoExpUid = useMemo(
    () => items.length > 0 && items.every(item => !item.expUid),
    [items]
  );

  const columns = useMemo(
    () => allColumns.filter(col => visibleColumnKeys.includes(col.key) && !(col.key === 'expUid' && hasNoExpUid)),
    [allColumns, visibleColumnKeys, hasNoExpUid]
  );

  return (
    <div className={summaryStyle.product_summary} style={{ minHeight: 0 }}>
      <CardHead
        title={ze('单条详情列表', 'Record Details')}
        svgIcon={<IconAllRecords />}
        hideDivider={!isExpanded}
        iconExtraStyle={{ height: '16px' }}
      >
        <div style={{ display: 'flex', flexGrow: 1, alignItems: 'center', justifyContent: 'flex-end' }}>
          {isExpanded && (
            <span
              style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: '#807997', fontSize: 13, marginRight: 16 }}
              onClick={(e) => {
                e.stopPropagation();
                setColumnConfigVisible(true);
              }}
            >
              <ConfiguredisplaycolumnsIcon style={{ height: 14, width: 14, marginRight: 4 }} />
              {ze('配置展示列', 'Configure Display Columns')}
            </span>
          )}
          <CsDownloadButton
            onClick={(e) => {
              e.stopPropagation();
              message.info(ze('下载功能开发中', 'Download in progress...'));
            }}
            style={{ marginRight: 16 }}
          />
          <CsCollapseButton
            isCollapsed={!isExpanded}
            enableHover={true}
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(prev => !prev);
            }}
          />
        </div>
      </CardHead>

      {isExpanded && (
        <>
          <Divider style={{ margin: '0px 0px 12px 0px' }} />
          <IssueCrashFilterEx
            platformId={platformId}
            hideIssueFields={true}
            isAdvancedTrend={true}
            filterOptions={filterOptions}
            fieldWhitelist={fieldWhitelist}
            fixedFieldList={[FieldName.crashUploadTime]}
            searchConditionGroup={{ conditions: pendingFilterConditions }}
            onChange={({ searchConditionGroup }) => {
              setPendingFilterConditions([...(searchConditionGroup?.conditions || [])]);
            }}
            onSubmit={() => onClickSearch()}
          />

          <Spin spinning={loading}>
            <Table
              columns={columns}
              dataSource={items}
              rowKey='_rowKey'
              size='small'
              scroll={{ x: 1800 }}
              pagination={{
                current: page,
                pageSize,
                total,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                showTotal: (t) => ze(`共 ${t} 条`, `Total ${t}`),
                onChange: (newPage, newPageSize) => {
                  setPage(newPage);
                  setPageSize(newPageSize);
                },
              }}
              style={{ fontSize: 13 }}
            />
          </Spin>
        </>
      )}

      <MetricKitFlameGraphModal
        visible={flameGraphModalVisible}
        data={flameGraphData}
        onClose={() => {
          setFlameGraphModalVisible(false);
          setFlameGraphData(null);
        }}
      />

      <Modal
        title={ze('配置展示列', 'Configure Display Columns')}
        open={columnConfigVisible}
        onOk={() => setColumnConfigVisible(false)}
        onCancel={() => setColumnConfigVisible(false)}
        width={480}
      >
        {/*
          注意：这里不能直接用 <Checkbox.Group value=...><Checkbox/>...</Checkbox.Group>。
          antd 给 Group 内相邻 .ant-checkbox-wrapper 加了 'margin-left: 8px' 的 inline-flow 样式，
          强制 flexDirection: column 后第二项起仍然有 8px 左 margin → 第一项缩进与其余不一致。
          改为：用普通 <div> 包每个 <Checkbox>，让 antd 的相邻选择器命中不到，自己手动维护选中态。
        */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {allColumns.map(c => (
            <div key={c.key}>
              <Checkbox
                checked={visibleColumnKeys.includes(c.key)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setVisibleColumnKeys(prev => prev.includes(c.key) ? prev : [...prev, c.key]);
                  } else {
                    setVisibleColumnKeys(prev => prev.filter(x => x !== c.key));
                  }
                }}
              >
                {c.title}
              </Checkbox>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
};

MetricKitDetailList.propTypes = {
  appId: PropTypes.string,
  platformId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** 上方 tab 当前选中的异常大类（CRASH/HANG/OOM 等，来自 ExceptionCategory enum）。会作为硬性条件加到查询里。 */
  exceptionCategory: PropTypes.string,
};

export default MetricKitDetailList;
